import React, { useMemo, useState } from 'react';
import {
  computeDiscreteColorBands,
  formatValue,
  areValuesEffectivelyEqual,
  getLayerNumClasses
} from '../../../utils/mapUtils';
import { getPaletteColors, getDefaultPalette, mapIntervalToColorIndex } from '../../../utils/colorPalettes';

const Colorbar = ({ layer, onFilterLayer }) => {
  const [hoveredBand, setHoveredBand] = useState(null);

  // Compute discrete color bands using shared function (same as MapPanel)
  // This ensures exact same colors and quantiles as the map rendering
  const { colorBands = [], isCategorical = false, hideColorScale = false } = useMemo(() => {
    const result = computeDiscreteColorBands(
      layer,
      getPaletteColors,
      mapIntervalToColorIndex,
      getDefaultPalette,
      getLayerNumClasses(layer)
    );
    return {
      colorBands: result.colorBands || [],
      isCategorical: result.isCategorical || false,
      hideColorScale: result.hideColorScale || layer.config?.highCardinalityCategorical || false
    };
  }, [layer]);

  if (hideColorScale || colorBands.length === 0) {
    return null;
  }

  const hasActiveFilter = layer.valueFilter != null;

  const handleBandClick = (band, index) => {
    if (onFilterLayer) {
      if (isCategorical) {
        // For categorical, filter by exact value match
        onFilterLayer(layer.id, {
          min: band.value,
          max: band.value,
          isLast: true
        });
      } else {
        const isLast = index === colorBands.length - 1;
        // Filter to show only hexes in this quantile range
        // For non-last bands, upper bound is exclusive (<)
        // For last band, upper bound is inclusive (<=)
        onFilterLayer(layer.id, {
          min: band.value,
          max: band.nextValue,
          isLast: isLast
        });
      }
    }
  };

  const handleClearFilter = (e) => {
    e.stopPropagation();
    onFilterLayer?.(layer.id, null);
  };

  return (
    <div className="flex flex-col gap-0.5">
      <div className="flex items-center justify-between gap-1.5 leading-none min-h-0">
        {hasActiveFilter ? (
          <>
            <span className="text-[8px] font-bold uppercase tracking-wider text-emerald-600 dark:text-emerald-400">
              Filtered
            </span>
            <button
              type="button"
              onClick={handleClearFilter}
              className="text-[8px] font-bold uppercase tracking-wider text-gray-500 hover:text-emerald-600 dark:text-gray-400 dark:hover:text-emerald-400 transition-colors shrink-0"
            >
              Reset filter
            </button>
          </>
        ) : (
          <span className="text-[8px] text-gray-400 dark:text-gray-500 font-medium">
            Click a class to filter
          </span>
        )}
      </div>
      {colorBands.map((band, index) => {
        const isHovered = hoveredBand === index;
        const isLast = index === colorBands.length - 1;
        const isFirst = index === 0;

        // Check if this band matches the active filter
        const isFiltered = layer.valueFilter && (
          isCategorical
            ? layer.valueFilter.min === band.value && layer.valueFilter.max === band.value
            : areValuesEffectivelyEqual(layer.valueFilter.min, band.value)
              && areValuesEffectivelyEqual(layer.valueFilter.max, band.nextValue)
        );

        const shouldGreyOut = hasActiveFilter && !isFiltered;

        // Format range label with comparison operators
        // For categorical, just show the value
        // Handle edge case: when all values are the same (band.value === band.nextValue)
        // Use tolerance-based comparison for floating point numbers
        const isSingleValue = areValuesEffectivelyEqual(band.value, band.nextValue);
        let rangeLabel;
        if (isCategorical || isSingleValue) {
          // Categorical or all values are the same, just show the value
          rangeLabel = formatValue(band.value);
        } else if (isLast) {
          rangeLabel = `≥ ${formatValue(band.value)}`;
        } else if (isFirst) {
          rangeLabel = `< ${formatValue(band.nextValue)}`;
        } else {
          rangeLabel = `≥ ${formatValue(band.value)} & < ${formatValue(band.nextValue)}`;
        }

        return (
          <div
            key={index}
            role="button"
            tabIndex={0}
            title="Show only this class on the map"
            className="flex items-center gap-1.5 cursor-pointer rounded -mx-0.5 px-0.5 py-px transition-colors group hover:bg-gray-100/80 dark:hover:bg-gray-800/60"
            onClick={() => handleBandClick(band, index)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleBandClick(band, index);
              }
            }}
            onMouseEnter={() => setHoveredBand(index)}
            onMouseLeave={() => setHoveredBand(null)}
          >
            {/* Color swatch */}
            <div
              className="w-3 h-3 rounded-sm border border-gray-300 dark:border-gray-600 flex-shrink-0"
              style={{
                backgroundColor: shouldGreyOut ? '#9ca3af' : band.color,
                opacity: shouldGreyOut ? 0.3 : isHovered ? 0.85 : 1,
                boxShadow: isFiltered ? '0 0 0 1.5px rgba(16, 185, 129, 0.7)' : 'none',
                filter: shouldGreyOut ? 'grayscale(100%)' : 'none'
              }}
            />
            <span
              className={`text-[9px] font-medium tabular-nums leading-none transition-colors ${
                shouldGreyOut
                  ? 'text-gray-400 dark:text-gray-600'
                  : isFiltered
                  ? 'text-emerald-600 dark:text-emerald-400 font-semibold'
                  : 'text-gray-600 dark:text-gray-300'
              }`}
            >
              {rangeLabel}
            </span>
          </div>
        );
      })}
    </div>
  );
};

export default Colorbar;
